import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import moment from 'moment';
import { Subscription } from 'rxjs';
import { Messages } from 'src/app/constants/messages';
import { Utilities } from 'src/app/constants/utilities';
import { CoordinatesModel, Entity, HierarchyModel, ZoneDataModel } from 'src/app/model';
import { ZonesServiceService } from 'src/app/modules/plants/plant-details/zones/zones-service.service';
import { DataService } from 'src/app/services/data.service';
import { ServicecallService } from 'src/app/services/servicecall.service';
import { FloorPlanService } from '../../floor-plan/service/floor-plan.service';
import { EntitySiteService } from '../../services';

@Component({
  selector: 'app-geo-trace-zone-details',
  templateUrl: './geo-trace-zone-details.component.html',
  styleUrls: ['./geo-trace-zone-details.component.css'],
})

export class GeoTraceZoneDetailsComponent implements OnInit, OnDestroy {
  availableZoneDetails: { name: string, id: string }[] = [];
  assignedZones: { name: string, id: string }[] = [];
  zoneForm: FormGroup;
  restrictedEntryZones = ['Restricted Entry Zones'];
  riskList = ['Low', 'Medium', 'High', 'Not Applicable'];
  hierarchyList: HierarchyModel[] = [];
  selectedTabIndex = 0;
  formSubmitted: boolean = false;
  errorMessage: string;
  showForm: boolean = false;
  tagFlag: boolean = false;
  untagFlag: boolean = false;
  timeZoneList: { name: string, value: string }[] = [];

  readonly MESSAGES = Messages;
  private _assignedZoneIds: string[] = [];
  private _assignedZoneDetails: { name: string, id: string };
  private _geoFenceCoordinates: { lat?: number | string, lng?: number | string, radius?: number | string }[] = [];
  private _zoneData: ZoneDataModel[] = [];
  private _timeZone: string;
  private _entityDetails: Entity;
  private _centroid: CoordinatesModel;
  private _subscription: Subscription;
  private _createNewZoneSubscription: Subscription;
  private _getZoneHierarchySubscription: Subscription;

  constructor(
    private _zoneService: ZonesServiceService,
    private _serviceCallService: ServicecallService,
    private _entityService: EntitySiteService,
    private _formBuilder: FormBuilder,
    private _dataService: DataService,
    private _floorPlanService: FloorPlanService
  ) {
    const timezoneLoc: string = moment?.tz?.guess();
    const timezoneFull = moment()?.tz(timezoneLoc)?.format();
    this._timeZone = timezoneFull?.substr(timezoneFull?.length - 6);

    this._subscription = this._dataService?.events?.subscribe((res: any) => {
      if (res?.name === 'zoneAdded') {
        if (res?.args?.[0]?.[1]?.radius) {
          this._geoFenceCoordinates = [{
            lat: res?.args?.[0]?.[0]?.latitude,
            lng: res?.args?.[0]?.[0]?.longitude
          },
          {
            radius: res?.args?.[0]?.[1]?.radius
          }];
        } else {
          let coordinate = [];
          res?.args?.[0].forEach((element) => {
            let geoFence = {
              lat: element?.lat?.toFixed(5),
              lng: element?.lng?.toFixed(5),
            };
            coordinate.push(geoFence);
          });
          this._geoFenceCoordinates = coordinate;
        }
        this._findCentroid(this._geoFenceCoordinates);
      } else if (res?.name === 'showZoneForm') {
        this.showForm = res?.args?.[0];
      }
    });
  }

  ngOnInit(): void {
    this._entityDetails = this._entityService?.getEntityDetails();
    this._addAssignedZones(this._entityDetails);
    this._initializeForm();
    this._getHierarchyList();
    this._configurations();
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
    this._createNewZoneSubscription?.unsubscribe();
    this._getZoneHierarchySubscription?.unsubscribe();
  }

  /**
   * Adds zone
   * @param selectedZone
   */
  addZone(selectedZone) {
    this.tagFlag = true;
    this.assignedZones?.push(selectedZone);
    this.availableZoneDetails = this.availableZoneDetails?.filter(
      (zone) => zone !== selectedZone
    );
    let zoneData = this._zoneData?.find(
      (zone: ZoneDataModel) => zone?.name === selectedZone?.name
    );
    let zoneDetails;
    if (zoneData?.geoFence?.geometry?.radius) {
      let coordinates = zoneData?.geoFence?.geometry?.center;
      zoneDetails = [
        {
          latitude: coordinates?.lat,
          longitude: coordinates?.lng,
        },
        { radius: zoneData?.geoFence?.geometry?.radius },
      ];
    } else {
      zoneDetails = zoneData?.geoFence?.geometry?.coordinates?.map((point: any) => [point?.lat, point?.lng]);
    }
    this._dataService?.broadcast('ExistingZoneAdded', [
      { name: selectedZone?.name, details: zoneDetails }
    ]);
    this._updateTagUntagFlags();
  }

  /**
   * Removes zone
   * @param selectedZone
   */
  removeZone(selectedZone) {
    this.untagFlag = true;
    this.availableZoneDetails.push(selectedZone);
    this.assignedZones = this.assignedZones?.filter(
      (zone) => zone !== selectedZone
    );
    let zoneDetails = this._zoneData?.find(
      (item: ZoneDataModel) => item?.name === selectedZone?.name
    )?.geoFence;
    this._dataService?.broadcast(
      'zoneRemoved',
      this._zoneData[1]?.radius
        ? zoneDetails
        : zoneDetails?.geometry?.coordinates?.map((point: any) => [point?.latitude, point?.longitude]),
      selectedZone?.name
    );
    this._updateTagUntagFlags();
  }

  /**
   * Determines whether submit on
   */
  onSubmit() {
    this.formSubmitted = true;
    this._createZone();
  }

  /**
   * Determines whether tab change on
   * @param event
   */
  onTabChange(event: any) {
    this.selectedTabIndex = event?.index;
  }

  /**
   * Tags entity to zone
   */
  tagEntityToZone() {
    this.tagFlag = false;
    this.untagFlag = false;
    const zoneIds = this.assignedZones?.map((zone) => zone?.id);
    const req = {
      entityId: this._entityDetails?._id,
      mappingId: this._entityDetails?.mappingId,
      zones: zoneIds,
    };
    this._floorPlanService.updateEntityLocation(req).subscribe({
      next: (result) => {
        this._serviceCallService.toasterService({
          type: 'success',
          msg: result?.message,
        });
        this._assignedZoneIds = this.assignedZones?.map((zone) => zone?.id);
      },
      error: (result) => {
        this._serviceCallService.toasterService({
          type: 'error',
          msg: result?.message,
        });
      },
    });
  }

  /**
   * Initializes form
   */
  private _initializeForm() {
    this.zoneForm = this._formBuilder.group({
      selectZone: ['Restricted Entry Zones', Validators.required],
      name: ['', Validators.required],
      level: [0, [Validators.required, Validators.pattern('^[+-]?[0-9][0-9]*$')]],
      centroid: this._formBuilder.group({
        lat: ['', [Validators.required, Validators.pattern(/^-?\d+\.\d{1,5}$/)]],
        lng: ['', [Validators.required, Validators.pattern(/^-?\d+\.\d{1,5}$/)]],
      }),
      attribute: this._formBuilder.group({
        riskType: [{ value: 'Low', disabled: true }],
        altitude: [{ value: '0.0', disabled: true }],
        maxPressure: [{ value: '0.0', disabled: true }],
        minPressure: [{ value: '0.0', disabled: true }],
        floorHeight: [{ value: '0.0', disabled: true }],
      }),
      geoFence: this._formBuilder.group({
        geometry: this._formBuilder.group({
          center: [{ lat: '', lng: '' }],
          radius: [''],
          coordinates: this._formBuilder.control([]),
          type: [''],
        }),
        properties: this._formBuilder.group({
          strokeOpacity: [{ value: '2.0', disabled: true }],
          strokeWeight: [{ value: '1.0', disabled: true }],
          strokeColor: [{ value: '#000', disabled: true }],
          fillColor: [{ value: '#000', disabled: true }],
          fillOpacity: [{ value: '5.0', disabled: true }],
          labelColor: [{ value: '#000', disabled: true }],
        }),
      }),
      zoneRestrictions: this._formBuilder.group({
        selectHierarchy: [null, Validators.required],
      }),
      restrictedAccess: this._formBuilder.group({
        status: [true],
        start: ['00:00'],
        timeZone: [this._timeZone, [Validators.required]],
        end: ['23:59'],
      }),
    });
  }

  /**
   * Zones change
   */
  private _getZoneList(): void {
    this.availableZoneDetails = [];
    const reqData = {
      siteId: this._entityDetails?.site?._id,
      classType: 'normal',
      sortBy: {
        order: 1,
      },
    };
    this._subscription = this._zoneService?.getZonesData(reqData)?.subscribe({
      next: (result) => {
        if (result['status']) {
          this._zoneData = result['response']['data'];
          this._assignedZoneIds = this.assignedZones?.map((zone) => zone?.id);
          this._zoneData.forEach((elem: any) => {
            if (!this._assignedZoneIds?.includes(elem?._id)) {
              this.availableZoneDetails?.push({ name: elem?.name, id: elem?._id });
            }
          });
        }
      },
      error: (result) => {
        this._serviceCallService?.toasterService({
          type: 'success',
          msg: result?.[0]?.['response']?.message,
        });
      },
    });
  }

  /**
   * Updates tag untag flags
   */
  private _updateTagUntagFlags() {
    const currentZoneIds = this.assignedZones?.map(zone => zone?.id);
    const zonesAdded = currentZoneIds?.some(id => !this._assignedZoneIds?.includes(id));
    const zonesRemoved = this._assignedZoneIds?.some(id => !currentZoneIds?.includes(id));
    this.tagFlag = zonesAdded;
    this.untagFlag = zonesRemoved;
  }

  /**
   * Finds centroid
   * @param coordinates
   */
  private _findCentroid(coordinates) {
    if (coordinates.length === 2 && coordinates[1]?.radius) {
      const circleCenter = coordinates[0];
      const circleCentroidLat = parseFloat(circleCenter?.lat);
      const circleCentroidLong = parseFloat(circleCenter?.lng);
      this._centroid = {
        lat: circleCentroidLat.toFixed(2),
        lng: circleCentroidLong.toFixed(2),
      };
    } else {
      let latTotal: number = 0;
      let logTotal: number = 0;
      let centroidLat: number = 0;
      let centroidLong: number = 0;
      const length = coordinates?.length;
      coordinates.forEach((element: { lat: string, lng: string }) => {
        latTotal += parseFloat(element?.lat);
        logTotal += parseFloat(element?.lng);
      });
      centroidLat = Math.round((latTotal / length + Number.EPSILON) * 100000) / 100000;
      centroidLong = Math.round((logTotal / length + Number.EPSILON) * 100000) / 100000;
      this._centroid = {
        lat: centroidLat?.toFixed(2),
        lng: centroidLong?.toFixed(2),
      };
    }
    this.zoneForm?.patchValue({
      centroid: this._centroid,
    });
  }

  /**
   * Creates zone
   */
  private _createZone() {
    if (this._geoFenceCoordinates?.[1]?.radius) {
      this.zoneForm?.patchValue({ geoFence: { geometry: { type: 'Circle' } } });
      this.zoneForm?.patchValue({ geoFence: { geometry: { center: this._geoFenceCoordinates?.[0] } } });
      this.zoneForm?.patchValue({ geoFence: { geometry: { radius: this._geoFenceCoordinates?.[1]?.radius } } });
      (this.zoneForm?.get('geoFence.geometry') as FormGroup).removeControl('coordinates');
      this.zoneForm?.removeControl('centroid');
    } else {
      this.zoneForm?.patchValue({ geoFence: { geometry: { type: 'Polygon' } } });
      this.zoneForm?.get('geoFence.geometry.coordinates').setValue(this._geoFenceCoordinates);
      (this.zoneForm?.get('geoFence.geometry') as FormGroup).removeControl('center');
      (this.zoneForm?.get('geoFence.geometry') as FormGroup).removeControl('radius');
    }
    let formValue = this.zoneForm?.getRawValue();
    formValue.properties = {
      destination: null,
      capacity: this.zoneForm?.value?.capacity,
      stack: this.zoneForm?.value?.stack,
    };
    if (this.zoneForm?.invalid) {
      return;
    }
    this._createNewZoneSubscription = this._zoneService
      .createNewZone(this._entityDetails?.site?._id, 'normal', null, formValue, null, null, null, null)
      .subscribe((result: any) => {
        this.formSubmitted = false;
        if (result['status']) {
          if (result['response']) {
            let zoneAddedDetails = {
              name: result?.response?.details?.name,
              id: result?.response?.details?._id,
            };
            this.assignedZones.push(zoneAddedDetails);
            this.showForm = false;
            this._updateTagUntagFlags();
            this._serviceCallService?.toasterService({
              type: 'success',
              msg: result['response']['message'],
            });
          } else {
            this.errorMessage =
              result['response']['message'] +
              ' - ' +
              result['response']['error'];
          }
        } else {
          this.formSubmitted = false;
          this.errorMessage =
            result['response']['message'] + ' - ' + result['response']['error'];
        }
      });
  }

  /**
   * Adds assigned zones
   * @param entityDetails
   */
  private _addAssignedZones(entityDetails: Entity) {
    if (entityDetails?.zoneDetails) {
      const zoneDetailsArray = [];
      entityDetails?.zoneDetails?.forEach((zone) => {
        this._assignedZoneDetails = { name: zone?.name, id: zone?.id };
        this.assignedZones.push(this._assignedZoneDetails);
        let zoneDetails;
        if (zone?.geoFence?.geometry?.radius) {
          let center = zone?.geoFence?.geometry?.center;
          zoneDetails = [
            {
              latitude: center?.lat,
              longitude: center?.lng,
            },
            { radius: zone?.geoFence?.geometry?.radius },
          ];
        } else {
          zoneDetails = zone?.geoFence?.geometry?.coordinates?.map((point: any) => [point?.lat, point?.lng]);
        }
        zoneDetailsArray.push({ name: zone?.name, details: zoneDetails });
      });
      setTimeout(() => {
        this._dataService?.broadcast('ExistingZoneAdded', zoneDetailsArray);
      }, 300);
    }
    this._getZoneList();
  }

  /**
   * Gets hierarchy list
   */
  private _getHierarchyList() {
    this.hierarchyList = [];
    const order = 30;
    this._getZoneHierarchySubscription = this._zoneService
      .getZoneHierarchy(this._entityDetails?.site?._id, order)
      .subscribe((res: any) => {
        this.hierarchyList = res['response']['data'];
      });
  }

  /**
   * Configurations geo trace zone details component
   */
  private _configurations() {
    this.timeZoneList = this._serviceCallService.getConfiguration('timeZoneList') ? this._serviceCallService.getConfiguration('timeZoneList')?.['timeZoneList'] : Utilities?.timeZoneListTemp;
  }

 /**
      * Reas check parent zone
      * @param event 
      */
  reaCheckParentZone(event: { isUserInput: any; source: { value: any; selected: any } }) {
    if (event?.isUserInput) {
      if (event?.source?.selected === true) {
        let hierarchyREA = this.hierarchyList?.find((zone: any) => zone?.name === event?.source?.value);
        this.zoneForm?.patchValue({
          restrictedAccess: {
            status: true,
            start: hierarchyREA?.restrictedAccess?.start,
            end: hierarchyREA?.restrictedAccess?.end,
            timeZone: hierarchyREA?.restrictedAccess?.timeZone
          }
        });
      }
    }
  }

  /**
 * Times validation
 */
  timeValidation() {
    if (this.zoneForm?.get('restrictedAccess.start')?.value && this.zoneForm?.get('restrictedAccess.end')?.value) {
      let start = this.zoneForm?.get('restrictedAccess.start')?.value;
      let end = this.zoneForm?.get('restrictedAccess.end')?.value;
      if (start === end) {
        this.errorMessage = 'start time should not be same as end time';
      } else if (start > end) {
        this.errorMessage = 'start time should not be greater than end time';
      } else {
        this.errorMessage = '';
      }
    }
  }
}
